import { isNumber, isString } from '../fold-helpers';
import { createWidgetNormalizer } from '../create-widget-normalizer';

import { IMAGE_SOURCE_KEYS } from './partial-image-widget-data.schema';

const URL_SYNONYM_KEYS = ['src', 'imageUrl', 'imageSrc'] as const;
const ALT_SYNONYM_KEYS = ['altText'] as const;
const LINK_SYNONYM_KEYS = ['href', 'linkUrl'] as const;
/**
 * Plain `width` means the pixel cap for an image (the editor's own width input writes
 * `styles.maxWidth`), unlike text where it means a percentage of the column.
 */
const MAX_WIDTH_SYNONYM_KEYS = ['width'] as const;
const RADIUS_SYNONYM_KEYS = ['radius', 'cornerRadius'] as const;

/**
 * Unwraps an `image` field into the flat source keys. This one is close to inevitable rather than
 * merely likely: the persisted field really IS called `data.image`, so a model that has seen the
 * entity — or just reasons from the widget's name — reaches for it, either as a bare URL string
 * (`image: "https://…"`) or as an object (`image: { url, alt }`). The flat schema has no `image`
 * key at all, and a plain `z.object()` strips unrecognized keys SILENTLY, so without this the whole
 * source would vanish: a hard "url required" failure on create, a success-reporting no-op on update.
 *
 * Only the six keys the flat schema actually exposes are lifted out of an object form; anything else
 * on it (CDN paths, srcsets, overlay-caption metadata) is file-manager state a model can't
 * legitimately author, so it's dropped rather than smuggled into the entity.
 *
 * Runs `before` the rules: the `url`/`alt`/`link` it lifts out are exactly what those rules then
 * treat as the already-set target, so a synonym can't overwrite a value the object form supplied.
 */
function foldImageObject(args: Record<string, unknown>, data: Record<string, unknown>): void {
    const image = data['image'] ?? args['image'];
    delete data['image'];

    if (typeof image === 'string') {
        if (data['url'] === undefined) {
            data['url'] = image;
        }
        return;
    }

    if (image == null || typeof image !== 'object' || Array.isArray(image)) {
        return;
    }

    for (const key of IMAGE_SOURCE_KEYS) {
        const value = (image as Record<string, unknown>)[key];

        if (value !== undefined && data[key] === undefined) {
            data[key] = value;
        }
    }
}

/**
 * The normalization shared by `create_image_widget` and `update_image_widget` — identical for both,
 * since an image patch and an image create body carry the same flat field surface (only `url`'s
 * requiredness differs, which the create schema tightens on its own).
 *
 * `extraFlatKeys` carries `image` and the six source keys because they are consumed by the bespoke
 * unwrap above rather than declared by a rule.
 */
const imageNormalizer = createWidgetNormalizer({
    before: [foldImageObject],
    rules: [
        { target: 'url', synonyms: URL_SYNONYM_KEYS, accepts: isString },
        { target: 'alt', synonyms: ALT_SYNONYM_KEYS, accepts: isString },
        { target: 'link', synonyms: LINK_SYNONYM_KEYS, accepts: isString },
        { target: 'maxWidth', synonyms: MAX_WIDTH_SYNONYM_KEYS, accepts: isNumber },
        { target: 'borderRadius', synonyms: RADIUS_SYNONYM_KEYS, accepts: isNumber },
    ],
    horizontalAlign: true,
    extraFlatKeys: ['image', ...IMAGE_SOURCE_KEYS],
});

export function normalizeImageWidgetArgs(raw: unknown): unknown {
    return imageNormalizer.normalize(raw);
}
